import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, BadgePercent, MousePointerClick, Shield, ShoppingBag, ShoppingCart, Store, Truck } from "lucide-react";
import { ProductCard } from "@/components/ProductCard";
import { SeoHead } from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { buildTrackedClickUrl, fetchProduct, fetchProductImages, fetchProducts } from "@/lib/api";
import { SITE_NAME, absoluteUrl, buildCategoryUrl, buildProductUrl, formatCurrencyLabel, sanitizeText, slugify, truncateText } from "@/lib/site";

const formatProductDescription = (value: string | null) => {
  if (!value) {
    return "";
  }

  const sectionTitles = [
    "Diferenciais",
    "Especificações técnicas",
    "Especificacoes tecnicas",
    "Potência dos queimadores",
    "Potencia dos queimadores",
    "Dimensões do produto",
    "Dimensoes do produto",
    "Medidas do nicho",
    "Conteúdo da embalagem",
    "Conteudo da embalagem",
    "Aviso legal",
  ];

  const fieldLabels = [
    "Marca",
    "Linha",
    "Modelo",
    "Cor",
    "Quantidade de bocas",
    "Tipo de instalação",
    "Tipo de instalacao",
    "Tipo de gás",
    "Tipo de gas",
    "Material da mesa",
    "Material das trempes",
    "Tipo de controle",
    "Voltagem",
    "Frequência",
    "Frequencia",
    "Altura",
    "Largura",
    "Profundidade",
    "Peso aproximado",
  ];

  let formatted = value
    .replace(/\r\n/g, "\n")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/([a-zà-ú])\.([A-ZÀ-Ú])/g, "$1.\n\n$2")
    .replace(/([a-zà-ú])([0-9]+ queimador)/gi, "$1\n$2")
    .replace(/(Preto|Branco|Inox|Bivolt|127\/220v|127\/220V)(O produto|O Cooktop|A sua|Com )/g, "$1\n\n$2");

  sectionTitles.forEach((title) => {
    formatted = formatted.replace(new RegExp(`\\s*${title}`, "g"), `\n\n${title}\n`);
  });

  fieldLabels.forEach((label) => {
    formatted = formatted.replace(new RegExp(`\\s*${label}:\\s*`, "g"), `\n${label}: `);
  });

  return formatted.replace(/\n{3,}/g, "\n\n").trim();
};

const ProductDetail = () => {
  const { id, slug } = useParams<{ id: string; slug?: string }>();
  const [selectedImg, setSelectedImg] = useState(0);
  const navigate = useNavigate();

  const { data: product, isLoading } = useQuery({
    queryKey: ["product", id],
    queryFn: () => fetchProduct(id!),
    enabled: Boolean(id),
  });

  const { data: extraImages = [] } = useQuery({
    queryKey: ["product-images", id],
    queryFn: () => fetchProductImages(id!),
    enabled: Boolean(id),
  });

  const { data: allProducts = [] } = useQuery({
    queryKey: ["products"],
    queryFn: () => fetchProducts(),
  });

  const expectedSlug = product ? slugify(product.item_name) : undefined;
  const canonicalPath = product ? buildProductUrl(product.id, product.item_name) : undefined;

  useEffect(() => {
    if (!id || !product || !expectedSlug || !canonicalPath) {
      return;
    }

    if (slug !== expectedSlug) {
      navigate(canonicalPath, { replace: true });
    }
  }, [canonicalPath, expectedSlug, id, navigate, product, slug]);

  const relatedProducts = useMemo(() => {
    if (!product) {
      return [];
    }

    return allProducts
      .filter((candidate) => candidate.id !== product.id && candidate.category && candidate.category === product.category)
      .slice(0, 10);
  }, [allProducts, product]);

  const allImages = useMemo(() => {
    const imageMap = new Map<string, string>();

    if (product?.image_url) {
      imageMap.set(product.image_url, product.image_url);
    }

    extraImages.forEach((image) => {
      imageMap.set(image.image_url, image.image_url);
    });

    return Array.from(imageMap.values());
  }, [extraImages, product?.image_url]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!product || !canonicalPath) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <SeoHead title="Produto nao encontrado" description="A oferta procurada nao esta disponivel neste momento." robots="noindex,follow" />
        <p className="text-muted-foreground">Produto nao encontrado.</p>
        <Link to="/"><Button variant="outline">Voltar para a home</Button></Link>
      </div>
    );
  }

  const displayPrice = formatCurrencyLabel(product.price);
  const offerRedirectUrl = buildTrackedClickUrl(product.id, "offer");
  const formattedDescription = formatProductDescription(product.description);
  const descriptionText = sanitizeText(formattedDescription || product.description) || `Confira detalhes, faixa de preco e oferta atualizada para ${product.item_name} na ${SITE_NAME}.`;
  const metaDescription = truncateText(
    `${product.item_name} com oferta, descricao e link direto para comprar melhor. ${descriptionText}`,
    160,
  );
  const productBenefits = [
    product.category ? `Categoria organizada em ${product.category}` : null,
    product.shop_name ? `Loja: ${product.shop_name}` : null,
    product.sales ? `${product.sales} vendidos` : "Oferta com acesso rapido a pagina do produto",
    product.national ? "Produto nacional identificado na vitrine" : null,
  ].filter(Boolean) as string[];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Inicio",
        item: absoluteUrl("/"),
      },
      ...(product.category ? [{
        "@type": "ListItem",
        position: 2,
        name: product.category,
        item: absoluteUrl(buildCategoryUrl(product.category)),
      }] : []),
      {
        "@type": "ListItem",
        position: product.category ? 3 : 2,
        name: product.item_name,
        item: absoluteUrl(canonicalPath),
      },
    ],
  };

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.item_name,
    description: descriptionText,
    image: allImages.length > 0 ? allImages.map((image) => absoluteUrl(image)) : undefined,
    sku: product.item_id,
    category: product.category || undefined,
    brand: product.shop_name ? { "@type": "Brand", name: product.shop_name } : undefined,
    offers: {
      "@type": "Offer",
      url: product.offer_link || product.product_link || absoluteUrl(canonicalPath),
      priceCurrency: "BRL",
      price: displayPrice.replace(/[^\d,.-]/g, "").replace(",", "."),
      availability: "https://schema.org/InStock",
      seller: product.shop_name ? { "@type": "Organization", name: product.shop_name } : { "@type": "Organization", name: SITE_NAME },
    },
  };

  const faqSchema = product.description ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `Vale a pena abrir a oferta de ${product.item_name}?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `A pagina mostra descricao, preco e acesso direto para conferir a oferta atual de ${product.item_name} na loja parceira.`,
        },
      },
      {
        "@type": "Question",
        name: "A NetLand Shop vende o produto diretamente?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Nao. A NetLand Shop funciona como uma vitrine de produtos e redireciona o usuario para a loja parceira.",
        },
      },
    ],
  } : undefined;

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col">
      <SeoHead
        title={product.item_name}
        description={metaDescription}
        canonicalPath={canonicalPath}
        image={allImages[0] || product.image_url || undefined}
        type="product"
        jsonLd={[breadcrumbSchema, productSchema, ...(faqSchema ? [faqSchema] : [])]}
      />

      <header className="sticky top-0 z-50 border-b bg-card/85 backdrop-blur-md">
        <div className="container mx-auto flex items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            <Link to="/">
              <Button variant="ghost" size="icon" aria-label="Voltar para a home">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <Link to="/" className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-primary" />
              <span className="text-lg font-bold text-foreground">{SITE_NAME}</span>
            </Link>
          </div>
          <Button asChild className="hidden sm:inline-flex">
            <a href={offerRedirectUrl} target="_blank" rel="noopener noreferrer sponsored">
              Ver oferta
            </a>
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 pb-12 flex-1">
        <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-2">
            <li><Link to="/" className="hover:text-primary transition-colors">Inicio</Link></li>
            {product.category ? (
              <>
                <li>/</li>
                <li>
                  <Link to={buildCategoryUrl(product.category)} className="hover:text-primary transition-colors">
                    {product.category}
                  </Link>
                </li>
              </>
            ) : null}
            {product.subcategory ? (
              <>
                <li>/</li>
                <li>
                  <Link
                    to={buildCategoryUrl(product.category || undefined, product.subcategory)}
                    className="hover:text-primary transition-colors"
                  >
                    {product.subcategory}
                  </Link>
                </li>
              </>
            ) : null}
            <li>/</li>
            <li className="text-foreground">{product.item_name}</li>
          </ol>
        </nav>

        <article className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <section className="rounded-2xl border bg-card p-4 md:p-6">
            <div className="space-y-3">
              <div className="aspect-square overflow-hidden rounded-xl bg-muted">
                {allImages.length > 0 ? (
                  <img
                    src={allImages[selectedImg] || allImages[0]}
                    alt={`Foto principal de ${product.item_name}`}
                    className="h-full w-full object-cover"
                    fetchPriority="high"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-muted-foreground">
                    <span className="text-6xl">[]</span>
                  </div>
                )}
              </div>

              {allImages.length > 1 ? (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {allImages.map((image, index) => (
                    <button
                      key={image}
                      onClick={() => setSelectedImg(index)}
                      className={`h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border-2 transition-colors ${
                        selectedImg === index ? "border-primary" : "border-muted"
                      }`}
                      aria-label={`Ver imagem ${index + 1} de ${product.item_name}`}
                    >
                      <img src={image} alt="" className="h-full w-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </section>

          <section className="rounded-2xl border bg-card p-4 md:p-6">
            <div className="space-y-5">
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {product.category ? (
                    <Link to={buildCategoryUrl(product.category)} className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                      {product.category}
                    </Link>
                  ) : null}
                  {product.national ? (
                    <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                      Produto nacional
                    </span>
                  ) : null}
                </div>

                <h1 className="text-2xl font-bold leading-tight text-foreground md:text-3xl">
                  {product.item_name}
                </h1>

                <p className="text-sm leading-6 text-muted-foreground">
                  {metaDescription}
                </p>
              </div>

              <div className="rounded-2xl border border-primary/15 bg-primary/5 p-4">
                <p className="text-sm font-medium text-muted-foreground">Faixa de preco da oferta</p>
                <p className="mt-1 text-3xl font-bold text-primary">{displayPrice}</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Confira disponibilidade, frete e variacoes diretamente na loja parceira.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex items-start gap-2 rounded-xl border bg-background p-3 text-sm text-muted-foreground">
                  <Store className="mt-0.5 h-4 w-4 text-primary" />
                  <div>
                    <p className="font-medium text-foreground">Loja</p>
                    <p>{product.shop_name || "Loja parceira"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 rounded-xl border bg-background p-3 text-sm text-muted-foreground">
                  <Truck className="mt-0.5 h-4 w-4 text-primary" />
                  <div>
                    <p className="font-medium text-foreground">Entrega</p>
                    <p>Consulte prazo e frete na loja.</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 rounded-xl border bg-background p-3 text-sm text-muted-foreground">
                  <Shield className="mt-0.5 h-4 w-4 text-primary" />
                  <div>
                    <p className="font-medium text-foreground">Compra segura</p>
                    <p>O redirecionamento abre a pagina da oferta em ambiente externo.</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 rounded-xl border bg-background p-3 text-sm text-muted-foreground">
                  <BadgePercent className="mt-0.5 h-4 w-4 text-primary" />
                  <div>
                    <p className="font-medium text-foreground">Oferta monitorada</p>
                    <p>Descricao e imagens foram organizadas para facilitar a decisao.</p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border bg-background p-4">
                <h2 className="text-base font-semibold text-foreground">Por que este produto chama atencao</h2>
                <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                  {productBenefits.map((benefit) => (
                    <li key={benefit} className="flex items-start gap-2">
                      <MousePointerClick className="mt-0.5 h-4 w-4 text-primary" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-3">
                <Button asChild size="lg" className="w-full gap-2">
                  <a href={offerRedirectUrl} target="_blank" rel="noopener noreferrer sponsored">
                    <ShoppingCart className="h-5 w-5" />
                    Ver oferta
                  </a>
                </Button>
              </div>
            </div>
          </section>
        </article>

        <section className="mt-6 rounded-2xl border bg-card p-4 md:p-6">
          <h2 className="text-xl font-semibold text-foreground">Descricao e contexto de uso</h2>
          <div className="mt-4 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
            <div>
              {formattedDescription ? (
                <p className="whitespace-pre-line text-sm leading-7 text-muted-foreground">
                  {formattedDescription}
                </p>
              ) : (
                <p className="text-sm leading-7 text-muted-foreground">
                  Confira os detalhes completos, variacoes e disponibilidade atual na loja parceira.
                </p>
              )}
            </div>
            <aside className="rounded-2xl border bg-background p-4">
              <h3 className="text-base font-semibold text-foreground">Resumo rapido</h3>
              <dl className="mt-3 space-y-3 text-sm">
                <div>
                  <dt className="font-medium text-foreground">Produto</dt>
                  <dd className="text-muted-foreground">{product.item_name}</dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">Categoria</dt>
                  <dd className="text-muted-foreground">{product.category || "Nao informada"}</dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">Subcategoria</dt>
                  <dd className="text-muted-foreground">{product.subcategory || "Nao informada"}</dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">Faixa de preco</dt>
                  <dd className="text-muted-foreground">{displayPrice}</dd>
                </div>
              </dl>
            </aside>
          </div>
        </section>

        {relatedProducts.length > 0 ? (
          <section className="mt-6">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-foreground">Produtos relacionados</h2>
              <p className="text-sm text-muted-foreground">
                Mais itens da mesma categoria para ajudar na comparacao e na escolha.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {relatedProducts.map((related) => (
                <ProductCard
                  key={related.id}
                  product={related}
                  onClick={() => navigate(buildProductUrl(related.id, related.item_name))}
                />
              ))}
            </div>
          </section>
        ) : null}
      </main>

      <footer className="border-t bg-card py-6">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          {new Date().getFullYear()} {SITE_NAME}. Vitrine de produtos e ofertas selecionadas.
        </div>
      </footer>
    </div>
  );
};

export default ProductDetail;
